import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ArrowLeft, HelpCircle, Lock, Loader2, Check } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ForgotPINPage = () => {
  const navigate = useNavigate();
  const { code } = useParams();
  const [securityQuestion, setSecurityQuestion] = useState("");
  const [securityAnswer, setSecurityAnswer] = useState("");
  const [newPin, setNewPin] = useState("");
  const [confirmPin, setConfirmPin] = useState("");
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchSecurityQuestion();
  }, [code]);

  const fetchSecurityQuestion = async () => {
    try {
      const response = await fetch(`${API}/qr/${code}/security-question`);
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || "Failed to fetch security question");
      }

      setSecurityQuestion(data.security_question);
    } catch (error) {
      toast.error(error.message);
      navigate("/dashboard");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!securityAnswer) {
      toast.error("Please enter your security answer");
      return;
    }
    if (newPin.length < 4 || newPin.length > 6 || !/^\d+$/.test(newPin)) {
      toast.error("PIN must be 4-6 digits");
      return;
    }
    if (newPin !== confirmPin) {
      toast.error("PINs do not match");
      return;
    }

    setSubmitting(true);
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${API}/qr/${code}/reset-pin`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        credentials: "include",
        body: JSON.stringify({
          security_answer: securityAnswer,
          new_pin: newPin,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || "Failed to reset PIN");
      }

      toast.success("PIN reset successfully!");
      navigate(`/edit/${code}`);
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-[#2563EB] border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8FAFC] pb-6">
      {/* Header */}
      <div className="bg-white px-6 py-4 shadow-sm">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate(-1)}
            data-testid="back-btn"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <p className="text-sm text-[#6B7280]">Reset PIN</p>
            <p className="font-bold text-[#1F2933] font-mono">{code}</p>
          </div>
        </div>
      </div>

      <div className="p-6">
        {/* Security Question */}
        <Card className="bg-white shadow-sm mb-4">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <HelpCircle className="w-5 h-5 text-[#2563EB]" />
              Security Question
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="bg-[#F8FAFC] rounded-lg p-4">
              <p className="text-[#1F2933] font-medium">{securityQuestion}</p>
            </div>
            <div className="space-y-2">
              <Label>Your Answer</Label>
              <Input
                placeholder="Enter your answer"
                value={securityAnswer}
                onChange={(e) => setSecurityAnswer(e.target.value)}
                className="h-12"
                data-testid="security-answer-input"
              />
            </div>
          </CardContent>
        </Card>

        {/* New PIN */}
        <Card className="bg-white shadow-sm">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Lock className="w-5 h-5 text-[#2563EB]" />
              Set New PIN
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label>New PIN (4-6 digits)</Label>
              <Input
                type="password"
                inputMode="numeric"
                maxLength={6}
                placeholder="••••••"
                value={newPin}
                onChange={(e) => setNewPin(e.target.value.replace(/\D/g, ""))}
                className="h-12 text-center text-xl tracking-widest"
                data-testid="new-pin-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Confirm New PIN</Label>
              <Input
                type="password"
                inputMode="numeric"
                maxLength={6}
                placeholder="••••••"
                value={confirmPin}
                onChange={(e) => setConfirmPin(e.target.value.replace(/\D/g, ""))}
                className="h-12 text-center text-xl tracking-widest"
                data-testid="confirm-new-pin-input"
              />
            </div>
            {newPin && confirmPin && newPin === confirmPin && (
              <div className="flex items-center gap-2 text-[#16A34A]">
                <Check className="w-4 h-4" />
                <span className="text-sm">PINs match</span>
              </div>
            )}
          </CardContent>
        </Card>

        <Button
          onClick={handleSubmit}
          disabled={submitting || !securityAnswer || !newPin || !confirmPin || newPin !== confirmPin}
          className="w-full h-12 mt-6 bg-[#2563EB] hover:bg-[#1D4ED8]"
          data-testid="reset-pin-btn"
        >
          {submitting ? (
            <Loader2 className="w-5 h-5 animate-spin mr-2" />
          ) : (
            <Lock className="w-5 h-5 mr-2" />
          )}
          Reset PIN
        </Button>
      </div>
    </div>
  );
};

export default ForgotPINPage;
